import { useCallback, useEffect, useState } from 'react'

import { DocumentTabPane } from '.'
import {
  closeUploadModal,
  openUploadModal,
  useFeatureDispatch,
  useFeatureState,
} from '../lib/state'
import { TabCategoryKeys as ModalTypes } from '../lib/constants'

import { RowTypes } from '../table/TableRows'
import { useAnalyticsTrack } from '@/features/amplitude'
import { UploadDocumentType } from '@/constants/businessConstants'
import { useDocumentUpload } from '@/components/documents/upload/hooks/useDocumentUpload'
import { UserDocumentsTable } from '../table'
import { DocumentSearchBarOnChangeData } from '../shared/DocumentsSearchBar'
import { TabPaneToolbar } from '../shared/TabPaneToolbar'
import { UploadDocumentButton } from '../shared/UploadDocumentButton'
import { DocumentUploadDialog, Text } from '@/ui'

const COLUMNS = [
  {
    sortBy: 'createdAt',
    title: 'Date uploaded',
  },
  {
    sortBy: 'fileName',
    title: 'File Name',
  },
  {
    sortBy: 'description',
    title: 'Description',
  },
  {
    sortBy: null,
    title: 'Actions',
  },
]

export const Other: DocumentTabPane = ({ documents, tableOptions }) => {
  const [filteredDocuments, setFilteredDocuments] = useState(documents)
  const { activeUploadModal, selectedDocument } = useFeatureState()
  const dispatch = useFeatureDispatch()
  const track = useAnalyticsTrack()

  const { uploadDocuments, isUploading } = useDocumentUpload({
    documentCategory: 'other',
    documentType: UploadDocumentType.OTHER,
    extraPayload: selectedDocument?.userDocumentCategoryId
      ? {
          userDocumentCategoryId: selectedDocument.userDocumentCategoryId,
          ...(selectedDocument.customerDescription && {
            customerDescription: selectedDocument.customerDescription,
          }),
        }
      : undefined,
    onUploadSuccess: (_uploadedDocuments) => {
      dispatch?.(closeUploadModal())
    },
    onUploadError: (errorMessage) => {
      console.error('Upload failed:', errorMessage)
    },
  })

  const handleDocumentsUpload = useCallback(
    async (documents: File[]) => {
      await uploadDocuments(documents)
    },
    [uploadDocuments]
  )

  useEffect(() => {
    setFilteredDocuments(documents)
  }, [documents])

  const onSearch = useCallback((data: DocumentSearchBarOnChangeData) => {
    setFilteredDocuments(data.filtered)
  }, [])

  const openModal = () => {
    track('clicked document upload', {
      document_category: ModalTypes.OTHER,
      document_subcategory: '',
    })
    dispatch?.(openUploadModal(ModalTypes.OTHER))
  }

  return (
    <>
      <TabPaneToolbar
        documents={documents}
        onSearch={onSearch}
        rightElement={<UploadDocumentButton onClick={openModal} />}
      >
        <Text>
          Upload any other documents you&apos;d like to share with our team.
        </Text>
      </TabPaneToolbar>

      <UserDocumentsTable
        columns={COLUMNS}
        documents={filteredDocuments}
        rowType={RowTypes.OTHER}
        tableOptions={tableOptions}
      />

      <DocumentUploadDialog
        isOpen={activeUploadModal === ModalTypes.OTHER}
        onClose={() => dispatch?.(closeUploadModal())}
        onUpload={handleDocumentsUpload}
        isUploadDisabled={isUploading}
        cardTitle="Upload Other Documents"
        cardDescription="Upload any other documents you'd like to share with our team."
      />
    </>
  )
}

Other.paneDescription =
  "Have anything else you want to share? Please use this section to upload any other documents you'd like to share with our team."
Other.paneTitle = 'Other'
